import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Private } from "../../components/Privacy";
import { dateLabel, plural, reportableBase } from "../../lib/finance";
import { figure } from "./insight-format";
import type { Transaction } from "../../types";
import type { InsightMerchant } from "./insight-types";

export default function MerchantInsights({ rows, purchases, currency, onOpenTransaction, onOpenMerchant }: {
  onOpenMerchant: (merchant: string) => void; rows: InsightMerchant[]; purchases: Transaction[]; currency: string; onOpenTransaction: (transaction: Transaction) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 5);
  return (
    <section className="panel insight-panel">
      <div className="section-heading"><div><h2>Where you spend</h2><p>Ranked by household spending · {currency}</p></div></div>
      {rows.some((row) => row.amount === null) && <p className="insight-note">Ranking is incomplete: merchants with missing exchange rates appear after known totals.</p>}
      {!rows.length ? (
        <p className="insight-empty">Your merchants will appear as you record purchases.</p>
      ) : (
        <ol className="insight-merchant-list">
          {visible.map((row, i) => (
            <li key={row.key}>
              <button
                type="button"
                className="insight-merchant-row-btn"
                onClick={() => onOpenMerchant(row.key)}
                aria-label={`View purchases at ${row.name}`}
              >
                <span className="insight-rank">{i + 1}</span>
                <span className="insight-merchant-name">
                  <strong>{row.name}</strong>
                  <small>· {plural(row.count, "purchase")}</small>
                </span>
                <span className="insight-merchant-amount">
                  <Private>{figure(row.amount, currency)}</Private>
                </span>
                <ChevronRight size={14} className="insight-row-arrow" />
              </button>
            </li>
          ))}
        </ol>
      )}
      {rows.length > 5 && (
        <button
          type="button"
          className="insight-text-button"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show fewer merchants" : `Show all ${rows.length} merchants`}
        </button>
      )}
      {purchases.length > 0 && (
        <div className="insight-purchases">
          <h3>{rows.some((row) => row.amount === null) ? "Largest purchases with known rates" : "Largest purchases"}</h3>
          {purchases.map((transaction) => (
            <div className="insight-purchase" key={transaction.id}>
              <button
                type="button"
                className="insight-purchase-btn"
                onClick={() => onOpenTransaction(transaction)}
              >
                <span className="insight-purchase-name">
                  <strong>{transaction.title || transaction.description || "Purchase"}</strong>
                  <small>· {dateLabel(transaction.date)}</small>
                </span>
                <span className="insight-purchase-amount">
                  <Private>{figure(reportableBase(transaction), currency)}</Private>
                </span>
                <ChevronRight size={14} className="insight-row-arrow" />
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="insight-note">Each purchase is counted once, even when split across categories.</p>
    </section>
  );
}
